// src/services/reports/NoiseReport.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AGDYSA_LOGO_BASE64 } from "../../assets/logoBase64";
import { NOISE_REGS, getNoiseActionText } from "../../utils/noiseFormulas";

export const generateNoiseReport = (segments, results, regKey, cms = null) => {
  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: "a4",
  });

  const margin = 20;
  const pageWidth = doc.internal.pageSize.width;
  const contentWidth = pageWidth - (margin * 2);
  const rightLimit = pageWidth - margin;
  const baseReg = NOISE_REGS[regKey];
  const cmsProt = cms?.ruido?.protocols?.[regKey];
  const reg = {
    ...baseReg,
    name: cmsProt?.name || baseReg.name,
    desc: cmsProt?.desc || baseReg.desc,
  };

  // 1. LOGO (PNG en Base64)
  try {
    const imgProps = doc.getImageProperties(AGDYSA_LOGO_BASE64);
    const logoWidth = 18.5;
    const logoHeight = (imgProps.height * logoWidth) / imgProps.width;
    doc.addImage(AGDYSA_LOGO_BASE64, "PNG", margin, 12, logoWidth, logoHeight);
  } catch (e) {
    console.error("Error al cargar el logo", e);
  }

  // 2. ENCABEZADO "AGDYSA" (Sombreado y Letra Gruesa)
  doc.setFillColor(235, 235, 235);
  doc.rect(margin + 28, 12, 100, 8, "F"); 
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(30, 90, 58);
  doc.text("AGDYSA", margin + 30, 18);

  // 3. SUBTÍTULO (Pequeño y Sombreado)
  doc.setFillColor(245, 245, 245);
  doc.rect(margin + 28, 20, 100, 5, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100);
  doc.text("REPORTE TÉCNICO DE EXPOSICIÓN A RUIDO", margin + 30, 23.5);

  // Línea divisoria
  doc.setDrawColor(30, 90, 58);
  doc.setLineWidth(0.5);
  doc.line(margin, 30, rightLimit, 30);

  // 4. INFORMACIÓN DE LA EVALUACIÓN
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text(`Fecha de Emisión: ${new Date().toLocaleDateString()}`, margin, 38);
  doc.text(`Protocolo Aplicado: ${reg.name}`, margin, 43);
  
  // Descripción de la norma (con salto de línea automático)
  doc.setFontSize(8);
  doc.setTextColor(100);
  const splitDesc = doc.splitTextToSize(`Descripción: ${reg.desc}`, contentWidth);
  doc.text(splitDesc, margin, 48);

  // 5. TABLA DE MEDICIONES POR TRAMO
  autoTable(doc, {
    startY: 55,
    margin: { left: margin, right: margin },
    head: [["UBICACIÓN / PROCESO", "NIVEL dB(A)", "TIEMPO", "DOSIS PARCIAL"]],
    body: results.segments.map(s => [
      s.loc || "No especificado",
      `${s.db} dB(A)`,
      `${s.hours || 0}h ${s.minutes || 0}m`,
      `${s.partialDose}%`
    ]),
    headStyles: { fillColor: [30, 90, 58], fontSize: 9, fontStyle: 'bold' },
    styles: { fontSize: 8, cellPadding: 3 },
    theme: "striped"
  });

  const finalY = doc.lastAutoTable.finalY + 15;

  // 6. RESULTADOS GLOBALES
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(30, 90, 58);
  doc.text("DIAGNÓSTICO FINAL DE LA JORNADA:", margin, finalY);

  // Caja de Resultado (Color dinámico según estatus)
  const isDanger = results.status === "Peligro";
  const isWarning = results.status === "Precaución";
  
  if (isDanger) doc.setFillColor(255, 240, 240);
  else if (isWarning) doc.setFillColor(255, 255, 240);
  else doc.setFillColor(240, 255, 240);
  
  doc.rect(margin, finalY + 3, contentWidth, 22, "F");
  
  // Texto Dosis y TWA
  doc.setFontSize(14);
  if (isDanger) doc.setTextColor(180, 0, 0);
  else doc.setTextColor(30, 90, 58);
  
  doc.text(`DOSIS TOTAL: ${results.dose}%`, margin + 5, finalY + 12);
  doc.setFontSize(10);
  doc.text(`NIVEL EQUIVALENTE: ${results.twa} dB(A) (${results.label})`, margin + 5, finalY + 18);

  // 7. ACCIÓN REQUERIDA
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.text("ACCIONES PREVENTIVAS RECOMENDADAS:", margin, finalY + 38);

  const actionText = getNoiseActionText(results.twa, regKey, cms?.ruido?.diagnostics);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  const splitAction = doc.splitTextToSize(actionText, contentWidth);
  doc.text(splitAction, margin, finalY + 45);

  // 8. CRITERIOS TÉCNICOS
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("PARÁMETROS DE CÁLCULO:", margin, finalY + 65);
  doc.setFont("helvetica", "normal");
  doc.text(`Tasa de Intercambio: ${reg.exrate} dB | Límite de Criterio: ${reg.crit} dB(A) | Tiempo de Referencia: 8 Horas`, margin, finalY + 70);

  // Pie de página
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text("Este informe es una herramienta de apoyo técnico generada por el sistema AGDYSA.", margin, 285);

  doc.save(`AGDYSA_Reporte_Ruido_${Date.now()}.pdf`);
};
/*<!--© 2026 Todos los derechos reservados Desarrollado por JEMAprod-Tecnologia-->*/